import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { Core as YubinBangoCore } from 'yubinbango-core2'
import BirthdayInput from '@/components/common/BirthdayInput'
import PageHeader from '@/components/common/PageHeader'

type Prefecture = {
  id: number
  name: string
}

type PatientFamilyProfileForm = {
  id: number | null
  first_name: string
  first_name_kana: string
  last_name: string
  last_name_kana: string
  birthday: string
  zip_code: string
  address_prefecture_id: number | null
  address_city: string
  address_street: string
  address_other: string
  phone_number: string
  sex: string
  basal_body_temperature: string
}

type Props = {
  mode: 'new' | 'edit'
  provisional_reservation_token: string
  patient_family_profile: PatientFamilyProfileForm
  prefectures: Prefecture[]
  errors?: Record<string, string[]>
}

const fieldLabelClass = 'mb-1 block text-sm font-medium text-[#2a3039]'
const fieldErrorClass = 'mt-1 text-sm text-red-600'

function FieldLabel({ text, required = true }: { text: string; required?: boolean }) {
  return (
    <div className="mb-1 flex items-center gap-2">
      <label className={fieldLabelClass}>{text}</label>
      {required ? (
        <span className="oc-badge-required">必須</span>
      ) : (
        <span className="oc-badge-optional">任意</span>
      )}
    </div>
  )
}

export default function PatientFamilyProfileFormPage({
  mode,
  provisional_reservation_token,
  patient_family_profile,
  prefectures,
  errors = {},
}: Props) {
  const [form, setForm] = useState<PatientFamilyProfileForm>(patient_family_profile)
  const [submitting, setSubmitting] = useState(false)

  const backPath = `/online_clinic/reservations/select_patient?provisional_reservation_token=${provisional_reservation_token}`

  const updateField = (key: keyof PatientFamilyProfileForm, value: string | number | null) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  useEffect(() => {
    if (!/^\d{7}$/.test(form.zip_code)) return

    new YubinBangoCore(form.zip_code, (addr: { region_id: number; locality: string; street: string; extended: string }) => {
      if (addr.region_id) {
        setForm((prev) => ({
          ...prev,
          address_prefecture_id: addr.region_id,
          address_city: addr.locality,
          address_street: addr.street,
          address_other: addr.extended || prev.address_other,
        }))
      }
    })
  }, [form.zip_code])

  const submit = () => {
    const payload = {
      provisional_reservation_token,
      patient_family_profile: form,
    }

    setSubmitting(true)

    if (mode === 'new') {
      router.post('/online_clinic/patient_family_profiles', payload, {
        onFinish: () => setSubmitting(false),
      })
      return
    }

    router.patch(`/online_clinic/patient_family_profiles/${form.id}`, payload, {
      onFinish: () => setSubmitting(false),
    })
  }

  const errorFor = (field: string) => errors[field]?.[0]
  const title = mode === 'new' ? '家族プロフィール追加' : '家族プロフィール編集'

  return (
    <>
      <Head title={`${title} - オンライン診療予約`} />

      <div className="min-h-screen bg-white">
        <PageHeader
          leading={
            <Link
              href={backPath}
              className="text-[#6f7680] hover:text-[#2a3039]"
              aria-label="前のページへ戻る"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
          }
          subtitle={title}
        />

        <main className="mx-auto max-w-3xl px-4 py-6">
          <div className="oc-card space-y-5">
            <h1 className="oc-section-title mb-0">{title}</h1>
            <p className="text-sm text-[#6f7680]">
              サービスをご利用される際には、プロフィールを登録してください。
              <br />
              ※オンラインクリニックをご利用される場合には、すべての項目の登録が必要です。
            </p>

            <div>
              <FieldLabel text="姓" />
              <input className="oc-input" value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} />
              {errorFor('first_name') && <p className={fieldErrorClass}>{errorFor('first_name')}</p>}
            </div>

            <div>
              <FieldLabel text="姓（フリガナ）" />
              <input className="oc-input" value={form.first_name_kana} onChange={(e) => updateField('first_name_kana', e.target.value)} />
              {errorFor('first_name_kana') && <p className={fieldErrorClass}>{errorFor('first_name_kana')}</p>}
            </div>

            <div>
              <FieldLabel text="名" />
              <input className="oc-input" value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} />
              {errorFor('last_name') && <p className={fieldErrorClass}>{errorFor('last_name')}</p>}
            </div>

            <div>
              <FieldLabel text="名（フリガナ）" />
              <input className="oc-input" value={form.last_name_kana} onChange={(e) => updateField('last_name_kana', e.target.value)} />
              {errorFor('last_name_kana') && <p className={fieldErrorClass}>{errorFor('last_name_kana')}</p>}
            </div>

            <div>
              <FieldLabel text="生年月日" />
              <BirthdayInput value={form.birthday} onChange={(value) => updateField('birthday', value)} />
              {errorFor('birthday') && <p className={fieldErrorClass}>{errorFor('birthday')}</p>}
            </div>

            <div>
              <FieldLabel text="性別" />
              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="radio"
                    className="oc-radio"
                    checked={form.sex === 'male'}
                    onChange={() => updateField('sex', 'male')}
                  />
                  <span>男性</span>
                </label>
                <label className="flex items-center gap-3">
                  <input
                    type="radio"
                    className="oc-radio"
                    checked={form.sex === 'female'}
                    onChange={() => updateField('sex', 'female')}
                  />
                  <span>女性</span>
                </label>
              </div>
              {errorFor('sex') && <p className={fieldErrorClass}>{errorFor('sex')}</p>}
            </div>

            <div>
              <FieldLabel text="郵便番号" />
              <input className="oc-input" value={form.zip_code} onChange={(e) => updateField('zip_code', e.target.value)} />
              {errorFor('zip_code') && <p className={fieldErrorClass}>{errorFor('zip_code')}</p>}
            </div>

            <p className="pt-2 text-base font-semibold text-[#2a3039]">住所</p>

            <div>
              <FieldLabel text="都道府県" />
              <select
                className="oc-select"
                value={form.address_prefecture_id ?? ''}
                onChange={(e) => updateField('address_prefecture_id', e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">選択してください</option>
                {prefectures.map((prefecture) => (
                  <option key={prefecture.id} value={prefecture.id}>
                    {prefecture.name}
                  </option>
                ))}
              </select>
              {errorFor('address_prefecture_id') && <p className={fieldErrorClass}>{errorFor('address_prefecture_id')}</p>}
            </div>

            <div>
              <FieldLabel text="市区町村" />
              <input className="oc-input" value={form.address_city} onChange={(e) => updateField('address_city', e.target.value)} />
              {errorFor('address_city') && <p className={fieldErrorClass}>{errorFor('address_city')}</p>}
            </div>

            <div>
              <FieldLabel text="町・番地" />
              <input className="oc-input" value={form.address_street} onChange={(e) => updateField('address_street', e.target.value)} />
              {errorFor('address_street') && <p className={fieldErrorClass}>{errorFor('address_street')}</p>}
            </div>

            <div>
              <FieldLabel text="建物名・部屋番号" required={false} />
              <input className="oc-input" value={form.address_other} onChange={(e) => updateField('address_other', e.target.value)} />
            </div>

            <div className="rounded-lg bg-[#f4fafe] p-3 text-sm text-[#2196f3]">
              保険診療の適用のため、医療保険資格情報に記載の住所を入力してください
            </div>

            <div>
              <FieldLabel text="携帯番号" />
              <input
                className="oc-input"
                type="tel"
                inputMode="numeric"
                maxLength={11}
                value={form.phone_number}
                onChange={(e) => updateField('phone_number', e.target.value)}
              />
              {errorFor('phone_number') && <p className={fieldErrorClass}>{errorFor('phone_number')}</p>}
            </div>

            <div>
              <FieldLabel text="基礎体温" required={false} />
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  step="0.1"
                  min="34.0"
                  max="42.0"
                  className="oc-input"
                  value={form.basal_body_temperature}
                  onChange={(e) => updateField('basal_body_temperature', e.target.value)}
                />
                <span className="text-[#515862]">℃</span>
              </div>
              {errorFor('basal_body_temperature') && <p className={fieldErrorClass}>{errorFor('basal_body_temperature')}</p>}
            </div>
          </div>

          <div className="oc-action-bar">
            <div className="oc-action-container">
              <Link href={backPath} className="button-secondary">
                戻る
              </Link>
              <button type="button" className="button-primary" disabled={submitting} onClick={submit}>
                {submitting ? '保存中...' : '保存して戻る'}
              </button>
            </div>
          </div>
          <div className="h-24" />
        </main>
      </div>
    </>
  )
}
